import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { FraudReviewStatus } from '../../fraud-dashboard.models';
import { buildCaseRows, FraudSessionExpiredError, parseFraudRows } from '../../fraud-dashboard.utils';
import { FraudCaseDetail, FraudCaseDetailView, FraudEvidence, FraudEvidenceRow, FraudReviewResult, FraudRoutePoint } from './fraud-case-details.models';
import { buildEvidenceRows } from './fraud-case-details.utils';
import axios from 'src/app/Service/axios';

@Component({
  selector: 'app-fraud-case-details',
  templateUrl: './fraud-case-details.component.html',
  styleUrls: ['./fraud-case-details.component.css']
})
export class FraudCaseDetailsComponent implements OnInit, OnDestroy {
  caseId = 0;
  detail: FraudCaseDetailView | null = null;
  evidence: FraudEvidenceRow[] = [];
  loading = false;
  evidenceLoading = false;
  routesLoading = false;
  saving = false;
  error = '';
  evidenceError = '';
  routesError = '';
  reviewError = '';
  reviewSuccess = '';
  reviewStatus: FraudReviewStatus = 0;
  reviewComment = '';
  routePoints: FraudRoutePoint[] | null = null;

  private subscription?: Subscription;
  private controller = new AbortController();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private gurdService: GurdServiceService,
    private other: OtherService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.subscription = this.route.queryParamMap.subscribe(params => {
      const id = params.get('id') || '';
      this.caseId = /^\d+$/.test(id) && Number.isSafeInteger(Number(id)) ? Number(id) : 0;
      void this.loadCase();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.controller.abort();
  }

  async loadCase(): Promise<void> {
    this.controller.abort();
    const controller = this.controller = new AbortController();
    this.detail = null;
    this.evidence = [];
    this.routePoints = null;
    this.error = this.evidenceError = this.routesError = this.reviewError = this.reviewSuccess = '';
    this.loading = this.caseId > 0;
    this.evidenceLoading = this.routesLoading = this.saving = false;
    if (!this.caseId) return;
    try {
      const rows = await this.request<FraudCaseDetail>('Fraud_MovementGroup_GetDetail', {}, controller.signal);
      if (controller.signal.aborted || !rows.length) return;
      const detail = rows[0];
      if (Number(detail.iFraudMovementGroup) !== this.caseId) throw new Error('Invalid case');
      this.detail = { ...detail, ...buildCaseRows([detail])[0] };
      this.reviewStatus = [0, 1, 2].includes(Number(detail.siReviewStatus)) ? Number(detail.siReviewStatus) as FraudReviewStatus : 0;
      this.reviewComment = detail.strReviewComment || '';
      void this.loadEvidence();
    } catch (error) {
      if (!controller.signal.aborted && !this.handleSessionError(error)) this.error = 'دریافت جزئیات پرونده با خطا مواجه شد. دوباره تلاش کنید.';
    } finally {
      if (!controller.signal.aborted) this.loading = false;
    }
  }

  async loadEvidence(): Promise<void> {
    if (!this.detail || this.evidenceLoading) return;
    const signal = this.controller.signal;
    this.evidenceLoading = true;
    this.evidenceError = '';
    try {
      const rows = await this.request<FraudEvidence>('Fraud_MovementGroup_GetEvidence', {}, signal);
      if (!signal.aborted) this.evidence = buildEvidenceRows(rows);
    } catch (error) {
      if (!signal.aborted && !this.handleSessionError(error)) this.evidenceError = 'دریافت شواهد با خطا مواجه شد. دوباره تلاش کنید.';
    } finally {
      if (!signal.aborted) this.evidenceLoading = false;
    }
  }

  async loadRoutes(): Promise<void> {
    if (!this.detail || this.routesLoading) return;
    this.routesError = '';
    if (typeof this.detail.strDate !== 'string' || !this.detail.strDate.trim()) {
      this.routesError = 'تاریخ پرونده برای دریافت مسیر در دسترس نیست.';
      return;
    }
    const signal = this.controller.signal;
    this.routesLoading = true;
    this.routePoints = null;
    try {
      const rows = await this.request<FraudRoutePoint>('Fraud_MovementGroup_GetRoutes', {
        strDate: this.detail.strDate
      }, signal);
      if (!signal.aborted) this.routePoints = rows;
    } catch (error) {
      if (!signal.aborted && !this.handleSessionError(error)) this.routesError = 'دریافت یا نمایش مسیر با خطا مواجه شد. دوباره تلاش کنید.';
    } finally {
      if (!signal.aborted) this.routesLoading = false;
    }
  }

  async saveReview(): Promise<void> {
    if (!this.detail || this.saving) return;
    this.reviewError = this.reviewSuccess = '';
    const status = this.reviewStatus, comment = this.reviewComment.trim();
    if (![0, 1, 2].includes(status) || comment.length > 500 || (status !== 0 && !comment)) {
      this.reviewError = comment.length > 500 ? 'توضیحات باید حداکثر ۵۰۰ کاراکتر باشد.' : 'برای تأیید یا رد پرونده، توضیحات ثبت کنید.';
      return;
    }
    const signal = this.controller.signal;
    this.saving = true;
    try {
      const rows = await this.request<FraudReviewResult>('Fraud_MovementGroup_UpdateReview', {
        siReviewStatus: status, strReviewComment: comment
      }, signal);
      if (signal.aborted) return;
      const result = rows[0];
      if (!result || Number(result.iFraudMovementGroup) !== this.caseId || ![0, 1, 2].includes(Number(result.siReviewStatus))) {
        throw new Error('Invalid review result');
      }
      this.reviewStatus = Number(result.siReviewStatus) as FraudReviewStatus;
      this.reviewComment = result.strReviewComment ?? comment;
      this.detail = { ...this.detail, ...buildCaseRows([{ ...this.detail, siReviewStatus: this.reviewStatus }])[0], strReviewComment: this.reviewComment };
      this.reviewSuccess = 'نتیجه بررسی ثبت شد.';
    } catch (error) {
      if (!signal.aborted && !this.handleSessionError(error)) this.reviewError = 'ثبت نتیجه بررسی با خطا مواجه شد. دوباره تلاش کنید.';
    } finally {
      if (!signal.aborted) this.saving = false;
    }
  }

  private async request<T>(endpoint: string, fields: Record<string, unknown>, signal: AbortSignal): Promise<T[]> {
    const user = Number(this.gurdService.decrypt(localStorage.getItem('ID')));
    const session = this.gurdService.decrypt(localStorage.getItem('Session'));
    if (!Number.isInteger(user) || user <= 0 || !session) throw new FraudSessionExpiredError();
    const response = await axios.post(endpoint, {
      iSystemUSer: user, strSession: session, iFraudMovementGroup: this.caseId, ...fields
    }, { timeout: 30000, signal });
    return parseFraudRows<T>(response.data);
  }

  private handleSessionError(error: unknown): boolean {
    if (!(error instanceof FraudSessionExpiredError)) return false;
    this.controller.abort();
    this.notification.showError('لطفا وارد سیستم شوید');
    this.other.removeLocalStorage();
    void this.router.navigate(['login'], { queryParams: { error: 'لطفا وارد سیستم شوید' } });
    return true;
  }

}
